import { getCountryCurrency, getCurrencySymbol } from "./formatters";

/**
 * Comprehensive country-based currency detection system
 * Ensures currency is always based on user's country, never defaults to UAE/AED
 */

// Auto-detect user's location using IP geolocation
export const detectUserLocation = async () => {
  try {
    console.log("Detecting user location via IP...");
    const response = await fetch("https://ipapi.co/json/");
    const data = await response.json();

    if (data.country_code && data.country_name) {
      const detectedCurrency = getCountryCurrency(
        data.country_name,
        data.country_code
      );

      console.log(
        `IP-based location detected: ${data.country_name} (${data.country_code})`
      );
      console.log(
        `Currency auto-detected: ${detectedCurrency.code} (${detectedCurrency.symbol})`
      );

      return {
        country: data.country_name,
        countryCode: data.country_code,
        currency: detectedCurrency.code,
        currencySymbol: detectedCurrency.symbol,
        detected: true,
      };
    }
  } catch (error) {
    console.warn("IP-based location detection failed:", error);
  }

  // Fallback to US if detection fails (most neutral default)
  const fallbackCurrency = getCountryCurrency("United States", "US");
  return {
    country: "United States",
    countryCode: "US",
    currency: fallbackCurrency.code,
    currencySymbol: fallbackCurrency.symbol,
    detected: false,
  };
};

// Set currency based on user's country
export const setCurrencyFromCountry = (country, countryCode = "") => {
  if (!country) {
    console.warn("No country provided for currency detection");
    return null;
  }

  // Fix country code mapping for common country names
  let finalCountryCode = countryCode;
  if (!countryCode && country) {
    const countryName = country.toLowerCase();
    
    // Map common country names to their codes
    const countryNameToCode = {
      'pakistan': 'PK',
      'united states': 'US',
      'united kingdom': 'GB',
      'india': 'IN',
      'saudi arabia': 'SA',
      'japan': 'JP',
      'canada': 'CA',
      'australia': 'AU',
      'switzerland': 'CH',
      'china': 'CN',
      'hong kong': 'HK',
      'singapore': 'SG',
      'malaysia': 'MY',
      'thailand': 'TH',
      'indonesia': 'ID',
      'south korea': 'KR',
      'brazil': 'BR',
      'russia': 'RU',
      'mexico': 'MX',
      'south africa': 'ZA',
      'turkey': 'TR',
      'new zealand': 'NZ',
      'sweden': 'SE',
      'norway': 'NO',
      'denmark': 'DK',
      'france': 'FR',
      'germany': 'DE',
      'italy': 'IT',
      'spain': 'ES',
      'netherlands': 'NL',
      'belgium': 'BE',
      'austria': 'AT',
      'portugal': 'PT',
      'ireland': 'IE',
      'greece': 'GR',
      'finland': 'FI',
      'poland': 'PL',
      'czech republic': 'CZ',
      'hungary': 'HU',
      'romania': 'RO',
      'bulgaria': 'BG',
      'croatia': 'HR',
      'ukraine': 'UA',
      'egypt': 'EG',
      'israel': 'IL',
      'jordan': 'JO',
      'kuwait': 'KW',
      'lebanon': 'LB',
      'oman': 'OM',
      'qatar': 'QA',
      'bahrain': 'BH',
      'iran': 'IR',
      'iraq': 'IQ',
      'afghanistan': 'AF',
      'bangladesh': 'BD',
      'sri lanka': 'LK',
      'nepal': 'NP',
      'philippines': 'PH',
      'vietnam': 'VN',
      'taiwan': 'TW',
      'mongolia': 'MN',
      'kazakhstan': 'KZ',
      'uzbekistan': 'UZ',
      'kyrgyzstan': 'KG',
      'tajikistan': 'TJ',
      'turkmenistan': 'TM',
      'azerbaijan': 'AZ',
      'armenia': 'AM',
      'georgia': 'GE',
      'belarus': 'BY',
      'moldova': 'MD',
      'serbia': 'RS',
      'bosnia and herzegovina': 'BA',
      'macedonia': 'MK',
      'albania': 'AL',
      'montenegro': 'ME',
      'iceland': 'IS',
      'morocco': 'MA',
      'algeria': 'DZ',
      'tunisia': 'TN',
      'libya': 'LY',
      'sudan': 'SD',
      'ethiopia': 'ET',
      'kenya': 'KE',
      'uganda': 'UG',
      'tanzania': 'TZ',
      'rwanda': 'RW',
      'ghana': 'GH',
      'nigeria': 'NG',
      'zimbabwe': 'ZW',
      'botswana': 'BW',
      'zambia': 'ZM',
      'mozambique': 'MZ',
      'madagascar': 'MG',
      'mauritius': 'MU'
    };
    
    finalCountryCode = countryNameToCode[countryName] || countryCode;
    console.log(`🔧 Mapped country name "${country}" to code "${finalCountryCode}"`);
  }

  const detectedCurrency = getCountryCurrency(country, finalCountryCode);

  // Update localStorage
  localStorage.setItem("currency", detectedCurrency.code);
  localStorage.setItem("currencySymbol", detectedCurrency.symbol);
  localStorage.setItem("country", country);
  if (finalCountryCode) {
    localStorage.setItem("countryCode", finalCountryCode);
  }

  console.log(
    `Currency set based on country: ${country} (${finalCountryCode}) -> ${detectedCurrency.code} (${detectedCurrency.symbol})`
  );

  // Dispatch custom event to notify other components
  window.dispatchEvent(
    new CustomEvent("currencyChanged", {
      detail: {
        country,
        countryCode: finalCountryCode,
        currency: detectedCurrency.code,
        currencySymbol: detectedCurrency.symbol,
      },
    })
  );

  return detectedCurrency;
};

// Get current currency settings, preferring country-based detection
export const getCurrentCurrencySettings = () => {
  const savedCountry = localStorage.getItem("country");
  const savedCountryCode = localStorage.getItem("countryCode");
  const savedCurrency = localStorage.getItem("currency");
  const savedSymbol = localStorage.getItem("currencySymbol");

  console.log(`🔍 Current localStorage values:`, {
    country: savedCountry,
    countryCode: savedCountryCode,
    currency: savedCurrency,
    currencySymbol: savedSymbol
  });

  // If we have country but currency doesn't match, or country code is wrong, re-detect
  if (savedCountry) {
    // Check if country code is wrong for the country name
    const countryName = savedCountry.toLowerCase();
    let expectedCountryCode = savedCountryCode;
    
    // Fix common country code mismatches
    if (countryName === 'pakistan' && savedCountryCode === 'US') {
      expectedCountryCode = 'PK';
      console.log(`🔧 Fixing country code mismatch: Pakistan should be PK, not US`);
    } else if (countryName === 'pakistan' && (!savedCountryCode || savedCountryCode !== 'PK')) {
      expectedCountryCode = 'PK';
      console.log(`🔧 Setting correct country code for Pakistan: ${savedCountryCode} -> PK`);
    }
    
    const expectedCurrency = getCountryCurrency(
      savedCountry,
      expectedCountryCode || ""
    );
    
    // Check if currency or country code needs fixing
    if (expectedCurrency.code !== savedCurrency || expectedCountryCode !== savedCountryCode) {
      console.log(
        `Currency/country code mismatch detected. Country: ${savedCountry}, Stored: ${savedCurrency} (${savedCountryCode}), Expected: ${expectedCurrency.code} (${expectedCountryCode})`
      );
      setCurrencyFromCountry(savedCountry, expectedCountryCode);
      return {
        country: savedCountry,
        countryCode: expectedCountryCode,
        currency: expectedCurrency.code,
        currencySymbol: expectedCurrency.symbol,
        isCountryBased: true,
      };
    }
  }

  // Ensure currency symbol is always correct from the currency mapping
  let finalCurrency = savedCurrency || "USD";
  let finalSymbol = savedSymbol;
  
  // Get correct symbol from currency mapping if we have currency
  if (finalCurrency) {
    const correctSymbol = getCurrencySymbol(finalCurrency);
    if (correctSymbol !== finalSymbol) {
      finalSymbol = correctSymbol;
      // Update localStorage with correct symbol
      localStorage.setItem("currencySymbol", correctSymbol);
      console.log(`🔧 Fixed currency symbol: ${finalCurrency} -> ${correctSymbol}`);
    }
  }

  const result = {
    country: savedCountry || "",
    countryCode: savedCountryCode || "",
    currency: finalCurrency,
    currencySymbol: finalSymbol || getCurrencySymbol(finalCurrency),
    isCountryBased: !!savedCountry,
  };

  console.log(`✅ Returning currency settings:`, result);
  return result;
};

// Initialize currency system on app startup
export const initializeCurrencySystem = async () => {
  const currentSettings = getCurrentCurrencySettings();

  // If no country is set, try to auto-detect
  if (!currentSettings.isCountryBased) {
    console.log("No country-based currency settings found. Auto-detecting...");
    const detectedLocation = await detectUserLocation();
    setCurrencyFromCountry(
      detectedLocation.country,
      detectedLocation.countryCode
    );
    return detectedLocation;
  }

  console.log(
    "Using existing country-based currency settings:",
    currentSettings
  );
  return currentSettings;
};

// Clear all currency settings (for logout)
export const clearCurrencySettings = () => {
  localStorage.removeItem("currency");
  localStorage.removeItem("currencySymbol");
  localStorage.removeItem("country");
  localStorage.removeItem("countryCode");
  console.log("Currency settings cleared");
};

// Ensure currency is never UAE unless user is actually from UAE
export const validateCurrencyNotUAE = () => {
  const currentSettings = getCurrentCurrencySettings();

  // If currency is AED but country is not UAE, reset to country-based currency
  if (
    currentSettings.currency === "AED" &&
    currentSettings.country !== "United Arab Emirates"
  ) {
    console.warn(
      "AED currency detected without UAE country. Resetting to country-based currency..."
    );
    if (currentSettings.country) {
      setCurrencyFromCountry(
        currentSettings.country,
        currentSettings.countryCode
      );
    } else {
      // Force detection if no country is set
      initializeCurrencySystem();
    }
  }
};

// Force currency refresh for current settings
export const forceCurrencyRefresh = () => {
  const savedCountry = localStorage.getItem("country");
  const savedCountryCode = localStorage.getItem("countryCode");
  
  if (savedCountry) {
    console.log(`🔄 Force refreshing currency for: ${savedCountry} (${savedCountryCode})`);
    setCurrencyFromCountry(savedCountry, savedCountryCode);
    
    // Trigger formatter refresh to update all components
    window.dispatchEvent(new CustomEvent("formatters-refresh"));
    
    return getCurrentCurrencySettings();
  }
  
  return null;
};

export default {
  detectUserLocation,
  setCurrencyFromCountry,
  getCurrentCurrencySettings,
  initializeCurrencySystem,
  clearCurrencySettings,
  validateCurrencyNotUAE,
  forceCurrencyRefresh,
};
